import { useCallback, useMemo } from 'react';

import jwtDecode from 'jwt-decode';
import endsWith from 'lodash/endsWith';
import find from 'lodash/find';
import get from 'lodash/get';

import { STORAGE_KEYS } from '@sbv-clients/util-misc';

const accessTokenKey = 'access_token';
const refreshTokenKey = 'refresh_token';
const sessionTokenKey = 'session_token';
export const useAccessToken = () => {
  const getAccessToken = useCallback(
    () => localStorage.getItem(accessTokenKey),
    []
  );

  const getRefreshToken = useCallback(
    () => localStorage.getItem(refreshTokenKey),
    []
  );

  const persistAuthTokens = useCallback(
    (accessToken: string, refreshToken: string) => {
      localStorage.setItem(accessTokenKey, accessToken);
      localStorage.setItem(refreshTokenKey, refreshToken);
    },
    []
  );

  const persistSessionToken = useCallback(() => {
    localStorage.setItem(sessionTokenKey, Date.now().toString());
  }, []);

  const removeAuthTokens = useCallback(() => {
    const keepSubstrings: string[] = [];
    Object.keys(localStorage).forEach((key) => {
      const shouldKeep = keepSubstrings.some((sub) => key.includes(sub));
      if (!shouldKeep) {
        localStorage.removeItem(key);
      }
    });
  }, []);

  const validateAccessTokenExpiry = useCallback((accessToken: string) => {
    if (!accessToken) {
      return false;
    }
    const decoded = jwtDecode(accessToken);
    const expiryDate = get(decoded, 'exp', 0);
    const currentTime = Date.now() / 1000;

    return expiryDate > currentTime;
  }, []);

  const getUserInfoFromToken = useCallback((token: string) => {
    try {
      const USER_NAME_KEY_POST_FIX = '/identity/claims/name';
      const ROKE_KEY_POST_FIX = '/identity/claims/role';
      const decoded = jwtDecode(token) as Record<string, never>;
      const userNameKey = find(Object.keys(decoded), (item) =>
        endsWith(item, USER_NAME_KEY_POST_FIX)
      ) as string;
      const roleKey = find(Object.keys(decoded), (item) =>
        endsWith(item, ROKE_KEY_POST_FIX)
      ) as string;
      const rawRole = decoded[roleKey] as string | string[] | undefined;
      const roles =
        rawRole == null ? [] : Array.isArray(rawRole) ? rawRole : [rawRole];
      return {
        userId: decoded['User.Identity.EmployeeId'],
        facilityId: decoded['User.Facility.FacilityId'],
        facilityEmployeeId: +decoded['User.Facility.FacilityEmployeeId'],
        facilityName: decoded['User.Facility.FacilityName'],
        impersonatorId: get(decoded, 'ImpersonatorId'),
        impersonatorUserName: get(decoded, 'ImpersonatorUserName'),
        isMultiFacility: get(decoded, 'IsMultiFacility') === 'True',
        useSchedulerWebsite: get(decoded, 'UseSchedulerWebsite') === 'True',
        username: decoded[userNameKey],
        roles,
      };
    } catch (e) {
      return {
        userId: '',
        facilityId: '',
        facilityEmployeeId: 0,
        facilityName: '',
        impersonatorId: '',
        impersonatorUserName: '',
        isMultiFacility: false,
        useSchedulerWebsite: false,
        username: '',
        roles: [] as string[],
      };
    }
  }, []);

  const parsedToken = useMemo(
    () => getUserInfoFromToken(getAccessToken() as string),
    [getAccessToken, getUserInfoFromToken]
  );

  return {
    getAccessToken,
    getRefreshToken,
    persistAuthTokens,
    persistSessionToken,
    removeAuthTokens,
    validateAccessTokenExpiry,
    getUserInfoFromToken,
    ...parsedToken,
  };
};

export default useAccessToken;
